import React from 'react';
import { Box, Grid, Button, Typography } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * RecipeCategories component displays different recipe sections in a 3x2 grid
 * 
 * @param {Object} props
 * @param {Array} props.recipeMenus - Array of recipe menu objects
 * @param {Function} props.onNavigate - Navigation function
 */
const RecipeCategories = ({ recipeMenus, onNavigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Create motion version of Button for animations
  const MotionButton = motion(Button);

  return (
    <Box sx={{ mb: { xs: 4, sm: 5 } }}>
      <Grid 
        container 
        spacing={2} 
        columns={6}
        sx={{ 
          px: { xs: 0, sm: 0 }
        }}
      >
        {recipeMenus.map((menu) => (
          <Grid 
            item 
            xs={2} 
            key={menu.name}
          >
            <MotionButton
              variant="contained"
              disableElevation
              fullWidth
              onClick={() => onNavigate(menu)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: isMobile ? '120px' : '150px',
                py: 2,
                px: 1,
                borderRadius: 3,
                textTransform: 'none',
                textAlign: 'center',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(66, 66, 66, 0.8)' 
                  : 'rgba(245, 245, 245, 0.9)',
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(80, 80, 80, 0.95)' 
                    : 'rgba(235, 235, 235, 0.95)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 0 15px rgba(150, 150, 150, 0.1)'
                    : '0 0 15px rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                height: '100%'
              }}>
                {/* Larger Icon */}
                <Box sx={{ 
                  fontSize: isMobile ? 36 : 44, 
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {React.cloneElement(menu.icon, { fontSize: 'inherit' })}
                </Box>
                
                {/* Menu Name */}
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    lineHeight: 1.2,
                    px: 0.5
                  }}
                >
                  {menu.name}
                </Typography>
                
                {/* Recipe Count */}
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}
                >
                  {menu.count} {menu.count === 1 ? 'recipe' : 'recipes'}
                </Typography>
              </Box>
            </MotionButton>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RecipeCategories; 